import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Phone, Menu, X, Film, BookOpen } from 'lucide-react';

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogoClick = (e) => {
    e.preventDefault();
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const handleNavClick = (e, sectionId) => {
    e.preventDefault();
    setMenuOpen(false);
    
    // If we're not on home page, navigate to home first then scroll
    if (location.pathname !== '/') {
      navigate('/#' + sectionId);
    } else {
      // Already on home page, just scroll
      const element = document.getElementById(sectionId);
      if (element) {
        const headerOffset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      }
    }
  };

  const handleBlogClick = (e) => {
    e.preventDefault();
    setMenuOpen(false);
    navigate('/blog');
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 supports-[backdrop-filter]:bg-white/80 supports-[backdrop-filter]:backdrop-blur-xl border-b-2 border-black" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <a href="/" onClick={handleLogoClick} className="flex items-center gap-1.5" data-testid="logo-link">
            <Film className="w-6 h-6 sm:w-7 sm:h-7 text-[#FF007A]" />
            <span className="font-bebas text-xl sm:text-2xl lg:text-3xl tracking-tighter leading-none">
              <span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(135deg, #F59E0B, #EA580C)' }}>VEGAS</span>
              <span className="font-caveat text-base sm:text-lg text-[#FF007A] mx-0.5">to</span>
              <span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(135deg, #06B6D4, #3B82F6)' }}>HOLLYWOOD</span>
            </span>
          </a>

          <nav className="hidden md:flex items-center gap-5 lg:gap-6" data-testid="desktop-nav">
            <a href="/#itinerary" onClick={(e) => handleNavClick(e, 'itinerary')} className="font-outfit text-sm uppercase tracking-wider font-medium hover:text-[#FF007A] transition-colors">Tour</a>
            <a href="/#reviews" onClick={(e) => handleNavClick(e, 'reviews')} className="font-outfit text-sm uppercase tracking-wider font-medium hover:text-[#FF007A] transition-colors">Reviews</a>
            <a href="/#faq" onClick={(e) => handleNavClick(e, 'faq')} className="font-outfit text-sm uppercase tracking-wider font-medium hover:text-[#FF007A] transition-colors">FAQ</a>
            <a href="/blog" onClick={handleBlogClick} className="font-outfit text-sm uppercase tracking-wider font-medium hover:text-[#FF007A] transition-colors flex items-center gap-1">
              <BookOpen className="w-4 h-4" />
              Blog
            </a>
            <a href="/#contact" onClick={(e) => handleNavClick(e, 'contact')} className="font-outfit text-sm uppercase tracking-wider font-medium hover:text-[#FF007A] transition-colors">Contact</a>
            <a href="tel:+18777418390" className="flex items-center gap-2 font-outfit font-bold text-sm" data-testid="header-phone">
              <Phone className="w-4 h-4 text-[#FF007A]" />
              1-877-741-8390
            </a>
            <a
              href="/#booking"
              onClick={(e) => handleNavClick(e, 'booking')}
              className="btn-primary px-5 py-2 text-sm"
              data-testid="header-book-now"
            >
              BOOK NOW
            </a>
          </nav>

          <div className="flex md:hidden items-center gap-3">
            <a href="tel:+18777418390" className="text-[#FF007A]" data-testid="header-phone-mobile">
              <Phone className="w-5 h-5" />
            </a>
            <button onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-toggle" className="p-1">
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="md:hidden border-t-2 border-black py-4 space-y-2 bg-white/95 backdrop-blur-xl" data-testid="mobile-menu">
            <a href="/#itinerary" onClick={(e) => handleNavClick(e, 'itinerary')} className="block font-outfit text-sm uppercase tracking-wider font-medium py-2 hover:text-[#FF007A]">Tour Details</a>
            <a href="/#reviews" onClick={(e) => handleNavClick(e, 'reviews')} className="block font-outfit text-sm uppercase tracking-wider font-medium py-2 hover:text-[#FF007A]">Reviews</a>
            <a href="/#faq" onClick={(e) => handleNavClick(e, 'faq')} className="block font-outfit text-sm uppercase tracking-wider font-medium py-2 hover:text-[#FF007A]">FAQ</a>
            <a href="/blog" onClick={handleBlogClick} className="block font-outfit text-sm uppercase tracking-wider font-medium py-2 hover:text-[#FF007A] flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              Travel Blog
            </a>
            <a href="/#contact" onClick={(e) => handleNavClick(e, 'contact')} className="block font-outfit text-sm uppercase tracking-wider font-medium py-2 hover:text-[#FF007A]">Contact</a>
            <a
              href="/#booking"
              onClick={(e) => handleNavClick(e, 'booking')}
              className="btn-primary block text-center px-6 py-3 text-sm mt-3"
              data-testid="mobile-book-now"
            >
              BOOK NOW - $215
            </a>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
